import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import SignatureCanvas from "react-signature-canvas";
import { jsPDF } from "jspdf";
import { useEmployee } from "@/context/EmployeeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Clock,
  Play,
  Square,
  Coffee,
  PenTool,
  Check,
  CalendarIcon,
  Building2,
  MapPin,
  Download,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
  const { employee } = useEmployee();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({ today_hours: 0, today_entries: 0 });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    start_time: "",
    end_time: "",
    break_minutes: 0,
    einrichtung: "",
    station_wb: "",
  });
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const signatureRef = useRef(null);

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  // Filter projects by type - only Einrichtungen needed for dropdown
  const einrichtungen = projects.filter(p => p.type === "einrichtung" || !p.type);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [entriesRes, projectsRes, statsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/time-entries?date=${dateStr}&employee_code=${employee.code}`),
        axios.get(`${API}/projects?employee_code=${employee.code}`),
        axios.get(`${API}/stats?employee_code=${employee.code}`),
        axios.get(`${API}/companies`),
      ]);
      setEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_time || !formData.end_time) {
      toast.error("Bitte Start- und Endzeit eingeben");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/time-entries`, {
        date: dateStr,
        start_time: formData.start_time,
        end_time: formData.end_time,
        break_minutes: parseInt(formData.break_minutes) || 0,
        einrichtung: formData.einrichtung || null,
        station_wb: formData.station_wb || null,
        employee_code: employee.code,
        employee_name: employee.name,
        company_name: employee.companyName,
      });
      toast.success("Zeiteintrag gespeichert");
      setFormData({ start_time: "", end_time: "", break_minutes: 0, einrichtung: "", station_wb: "" });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsLoading(false);
    }
  };

  const openSignatureModal = (entry) => {
    setSelectedEntry(entry);
    setSignerName("");
    setSignatureModalOpen(true);
  };

  const calculateWorkTime = (start, end, breakMins) => {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    
    // Handle night shifts (end time is next day)
    if (endMins < startMins) {
      endMins += 24 * 60; // Add 24 hours
    }
    
    const totalMins = endMins - startMins - breakMins;
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return { hours, mins, totalMins, formatted: `${hours}h ${mins}m` };
  };

  const generatePDF = (entry, signatureData, name) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("ZeitFlow", 20, 25);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Arbeitszeitnachweis", 20, 33);
    
    // Content
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Arbeitszeiterfassung", 20, 55);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    const entryDate = format(new Date(entry.date), "EEEE, d. MMMM yyyy", { locale: de });
    const workTime = calculateWorkTime(entry.start_time, entry.end_time, entry.break_minutes);
    
    // Use employee name/company from entry if available, otherwise from current user
    const employeeName = entry.employee_name || employee.name;
    const companyName = entry.company_name || employee.companyName;
    
    // Get company address
    const company = companies.find(c => c.name === companyName || c.code === employee.companyCode);
    const companyAddress = company ? 
      [company.street, `${company.zip_code || ''} ${company.city || ''}`.trim()].filter(Boolean).join(", ") : "";
    
    const details = [
      { label: "Mitarbeiter:", value: employeeName },
      { label: "Firma:", value: companyName },
    ];
    
    // Add address if available
    if (companyAddress) {
      details.push({ label: "Adresse:", value: companyAddress });
    }
    
    details.push(
      { label: "Datum:", value: entryDate },
      { label: "Arbeitsbeginn:", value: entry.start_time + " Uhr" },
      { label: "Arbeitsende:", value: entry.end_time + " Uhr" },
      { label: "Pause:", value: entry.break_minutes + " Minuten" },
      { label: "Netto-Arbeitszeit:", value: workTime.formatted },
      { label: "Einrichtung:", value: entry.einrichtung || "Keine Angabe" },
      { label: "Station/WB:", value: entry.station_wb || "Keine Angabe" },
    );
    
    let yPos = 70;
    details.forEach(({ label, value }) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value || "", 80, yPos);
      yPos += 10;
    });
    
    // Signature Section
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos + 10, pageWidth - 20, yPos + 10);
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Unterschrift", 20, yPos + 25);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Name des Unterzeichners:", 20, yPos + 40);
    doc.setFont("helvetica", "bold");
    doc.text(name, 80, yPos + 40);
    
    doc.setFont("helvetica", "normal");
    doc.text("Unterschrieben am:", 20, yPos + 50);
    doc.text(format(new Date(), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de }), 80, yPos + 50);
    
    // Add signature image
    if (signatureData) {
      doc.addImage(signatureData, 'PNG', 20, yPos + 60, 80, 40);
    }
    
    // Footer with legal notice
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 260, pageWidth - 20, 260);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text("Gemäß Arbeitszeitgesetz (ArbZG) darf die werktägliche Arbeitszeit von 10 Std. nicht überschritten werden.", 20, 268);
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 272, pageWidth - 20, 272);
    doc.setFontSize(9);
    doc.text("Erstellt mit ZeitFlow - Arbeitszeiterfassung", 20, 280);
    doc.text(`Dokument erstellt am: ${format(new Date(), "dd.MM.yyyy HH:mm")}`, pageWidth - 20 - 60, 280);
    
    // Get filename and PDF as base64
    const fileName = `Arbeitszeit_${entry.date}_${name.replace(/\s+/g, '_')}.pdf`;
    const pdfBase64 = doc.output('datauristring').split(',')[1];
    
    // Download locally
    doc.save(fileName);
    
    return { fileName, pdfBase64 };
  };

  const sendPDFByEmail = async (pdfBase64, filename, entry) => {
    try {
      const response = await axios.post(`${API}/send-pdf-email`, {
        pdf_base64: pdfBase64,
        filename: filename,
        company_code: employee.companyCode || employee.companyName,
        employee_name: employee.name,
        date: format(new Date(entry.date), "dd.MM.yyyy", { locale: de }),
      });
      toast.success(`PDF per E-Mail gesendet an ${response.data.email}`);
    } catch (error) {
      console.error("Email sending failed:", error);
      toast.error("PDF konnte nicht per E-Mail gesendet werden");
    }
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast.error("Bitte geben Sie Ihren Namen ein");
      return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Bitte unterschreiben Sie zuerst");
      return;
    }
    
    setIsLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL("image/png");
      
      // Save signature to backend
      await axios.put(`${API}/time-entries/${selectedEntry.id}/sign`, {
        signature: signatureData,
        signer_name: signerName,
      });
      
      // Generate and download PDF
      const { fileName, pdfBase64 } = generatePDF(selectedEntry, signatureData, signerName);
      
      toast.success(`Unterschrift gespeichert! PDF "${fileName}" wird heruntergeladen...`);
      
      // Send PDF via email automatically
      await sendPDFByEmail(pdfBase64, fileName, selectedEntry);
      
      setSignatureModalOpen(false);
      setSignerName("");
      signatureRef.current?.clear();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern der Unterschrift");
    } finally {
      setIsLoading(false);
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight font-['Manrope']" data-testid="dashboard-title">
            Übersicht
          </h2>
          <p className="text-slate-500 mt-1">
            {format(selectedDate, "EEEE, d. MMMM yyyy", { locale: de })}
          </p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="date-picker-trigger">
              <CalendarIcon className="w-4 h-4" />
              {format(selectedDate, "dd.MM.yyyy")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={de}
              data-testid="date-calendar"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Timer Hero Card */}
        <Card className="md:col-span-8 zeitflow-card animate-fadeIn stagger-1" data-testid="time-entry-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-semibold">
              <Clock className="w-5 h-5 text-blue-600" />
              Neue Zeiterfassung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Time inputs row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time" className="text-sm font-medium text-slate-700">
                    <Play className="w-4 h-4 inline mr-1 text-green-600" />
                    Arbeitsbeginn
                  </Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="time-input"
                    data-testid="start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time" className="text-sm font-medium text-slate-700">
                    <Square className="w-4 h-4 inline mr-1 text-red-600" />
                    Arbeitsende
                  </Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="time-input"
                    data-testid="end-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="break_minutes" className="text-sm font-medium text-slate-700">
                    <Coffee className="w-4 h-4 inline mr-1 text-amber-600" />
                    Pause (Min.)
                  </Label>
                  <Input
                    id="break_minutes"
                    type="number"
                    min="0"
                    value={formData.break_minutes}
                    onChange={(e) => setFormData({ ...formData, break_minutes: e.target.value })}
                    data-testid="break-minutes-input"
                  />
                </div>
              </div>

              {/* Location inputs row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    <Building2 className="w-4 h-4 inline mr-1 text-purple-600" />
                    Einrichtung
                  </Label>
                  <Select 
                    value={formData.einrichtung} 
                    onValueChange={(value) => setFormData({ ...formData, einrichtung: value, station_wb: "" })}
                  >
                    <SelectTrigger data-testid="einrichtung-select">
                      <SelectValue placeholder="Einrichtung auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {einrichtungen.map((e) => (
                        <SelectItem key={e.id} value={e.name}>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: e.color }} />
                            {e.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    <MapPin className="w-4 h-4 inline mr-1 text-blue-600" />
                    Station / WB
                  </Label>
                  <Input
                    type="text"
                    value={formData.station_wb}
                    onChange={(e) => setFormData({ ...formData, station_wb: e.target.value })}
                    placeholder="z.B. Station A, WB Nord"
                    data-testid="station-wb-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
                data-testid="save-entry-button"
              >
                <Check className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
          <Card className="zeitflow-card animate-fadeIn stagger-2" data-testid="stats-hours">
            <CardContent className="pt-6">
              <div className="stats-value text-blue-600">{stats.today_hours}h</div>
              <div className="stats-label">Stunden heute</div>
            </CardContent>
          </Card>
          <Card className="zeitflow-card animate-fadeIn stagger-3" data-testid="stats-entries">
            <CardContent className="pt-6">
              <div className="stats-value">{stats.today_entries}</div>
              <div className="stats-label">Einträge heute</div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Entries */}
        <Card className="md:col-span-12 zeitflow-card animate-fadeIn stagger-4" data-testid="entries-list">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">Heutige Einträge</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Noch keine Einträge für diesen Tag</p>
              </div>
            ) : (
              <div className="space-y-3">
                {entries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="entry-item flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-slate-200 gap-4"
                    data-testid={`entry-item-${index}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="text-lg font-semibold text-slate-900 tabular-nums">
                        {entry.start_time} - {entry.end_time}
                      </div>
                      {entry.einrichtung && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                          <Building2 className="w-3 h-3" />
                          {entry.einrichtung}
                        </div>
                      )}
                      {entry.station_wb && (
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          <MapPin className="w-3 h-3" />
                          {entry.station_wb}
                        </div>
                      )}
                      <div className="text-slate-500 text-sm">
                        <Coffee className="w-3 h-3 inline mr-1" />
                        {entry.break_minutes} Min. Pause
                      </div>
                      <div className="font-medium text-blue-600">
                        {calculateWorkTime(entry.start_time, entry.end_time, entry.break_minutes).formatted}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.signature ? (
                        <span className="signed-badge" data-testid={`signed-badge-${index}`}>
                          <Check className="w-3 h-3 inline mr-1" />
                          {entry.signer_name ? `${entry.signer_name}` : "Unterschrieben"}
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSignatureModal(entry)}
                          className="gap-1"
                          data-testid={`sign-button-${index}`}
                        >
                          <PenTool className="w-4 h-4" />
                          Unterschreiben
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Signature Modal */}
      <Dialog open={signatureModalOpen} onOpenChange={setSignatureModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="signature-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-blue-600" />
              Tagesabschluss unterschreiben
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="signer_name" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-600" />
                Name des Unterzeichners
              </Label>
              <Input
                id="signer_name"
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Vor- und Nachname eingeben"
                className="w-full"
                data-testid="signer-name-input"
              />
            </div>
            
            {/* Signature Canvas */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                Unterschrift
              </Label>
              <p className="text-sm text-slate-500">
                Bitte unterschreiben Sie im Feld unten. Nach der Bestätigung wird automatisch eine PDF erstellt.
              </p>
              <div className="border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 overflow-hidden">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: "signature-canvas w-full h-48",
                    "data-testid": "signature-canvas",
                  }}
                  backgroundColor="rgb(248, 250, 252)"
                />
              </div>
            </div>
            
            {/* PDF Info */}
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <Download className="w-4 h-4 flex-shrink-0" />
              <span>Nach der Unterschrift wird automatisch eine PDF mit allen Details heruntergeladen.</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={clearSignature} data-testid="clear-signature-button">
              Löschen
            </Button>
            <Button
              onClick={handleSign}
              disabled={isLoading || !signerName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-signature-button"
            >
              <Check className="w-4 h-4 mr-2" />
              Bestätigen & PDF erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
